import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  FormControlLabel,
  Switch,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Badge as BadgeIcon,
  CloudUpload as CloudUploadIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Work as WorkIcon,
  Phone as ContactIcon,
  SupervisorAccount as SupervisorIcon,
  Settings as SettingsIcon,
  Description as DocsIcon,
  Group as GroupIcon,
  Email as EmailIcon,
  FamilyRestroom as FamilyIcon,
  Emergency as EmergencyIcon,
  Save as SaveIcon,
  Lock as LockIcon,
  Fingerprint as BiometricIcon,
  Send as SendIcon,
  AdminPanelSettings as RolesIcon
} from '@mui/icons-material';
import { fetchEmployeeProfile, clearSelectedEmployee } from '../../store/slices/employeeSlice';
import { setUser } from '../../store/slices/authSlice';
import { employeeApi } from '../../api/employeeApi';
import { authApi } from '../../api/authApi';
import { masterApi } from '../../api/masterApi';
import { documentManagementApi, DocumentTypeItem } from '../../api/documentManagementApi';
import { rolesApi, EmployeeProfileRoleLookupDto } from '../../api/rolesApi';
import { shiftApi } from '../../api/shiftApi';
import { administrationApi } from '../../api/administrationApi';
import { AppDispatch, RootState } from '../../store/store';
import { EmploymentStatusLabels, EmploymentStatusColors, DropdownItem } from '../../types';
import { getUploadsBaseUrl } from '../../config/runtimeConfig';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import EmployeeProfilePhotoUpload from '../EmployeeProfilePhotoUpload';
import DocumentUpload from '../DocumentUpload';
import { BlobCategory } from '../../api/azureBlobStorageApi';
import LanguagePreferenceSelect from '../LanguagePreferenceSelect';
//import { DocumentTypeItem } from '../../api/documentManagementApi';

// ── All sections (full list, filtered per role below) ──────────────────────
const ADMIN_ONLY_SECTION_KEYS = new Set(['roles', 'job', 'supervisor', 'settings', 'email']);

const ALL_PROFILE_SECTIONS = [
  { key: 'roles', label: 'Roles', icon: <RolesIcon /> },
  { key: 'basic', label: 'Basic Details', icon: <PersonIcon /> },
  { key: 'job', label: 'Job Details', icon: <WorkIcon /> },
  { key: 'contact', label: 'Contact Details', icon: <ContactIcon /> },
  { key: 'supervisor', label: 'Superior Mapping', icon: <SupervisorIcon /> },
  { key: 'settings', label: 'Employee Settings', icon: <SettingsIcon /> },
  { key: 'emergency', label: 'Emergency Contact', icon: <EmergencyIcon /> },
  { key: 'dependants', label: 'Dependants Details', icon: <FamilyIcon /> },
  { key: 'email', label: 'Email Subscription', icon: <EmailIcon /> },
  { key: 'documents', label: 'Documents', icon: <DocsIcon /> },
];
// Helper component for dropdown fields
const DropdownField: React.FC<{
  label: string;
  value: string;
  options: DropdownItem[];
  onChange: (val: string) => void;
  disabled?: boolean;
}> = ({ label, value, options, onChange, disabled = false }) => (
  <TextField
    label={label}
    select
    fullWidth
    size="small"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
  >
    <MenuItem value="">Select</MenuItem>
    {options.map((o) => (
      <MenuItem key={String(o.id)} value={String(o.id)}>
        {o.name}
      </MenuItem>
    ))}
  </TextField>
);

const getRelationshipLabel = (
  relationshipId: string | number | null | undefined,
  options: DropdownItem[],
) => {
  if (relationshipId === null || relationshipId === undefined || relationshipId === '') {
    return '-';
  }

  const match = options.find((option) => String(option.id) === String(relationshipId));
  return match?.name || String(relationshipId);
};

const normalizeNumericInput = (value: string, maxLength: number) =>
  value.replace(/\D/g, '').slice(0, maxLength);

const getSelectedOption = (
  id?: string | number | null,
  name?: string | null
): DropdownItem | null => {
  if (id === null || id === undefined || id === '') return null;
  return { id, name: name || String(id) };
};

const mergeSelectedOption = (
  options: DropdownItem[],
  selected: DropdownItem | null
) => {
  if (!selected) return options;

  const selectedId = String(selected.id);
  if (options.some((option) => String(option.id) === selectedId)) {
    return options;
  }

  return [selected, ...options];
};

const getDocumentUrl = (filePath?: string | null) => {
  if (!filePath) return '';
  if (/^https?:\/\//i.test(filePath)) return filePath;
  return `${getUploadsBaseUrl()}/${filePath.replace(/^\/+/, '')}`;
};

const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const getEmailError = (value: string, label = 'Email') => {
  if (!value.trim()) return '';
  return isValidEmail(value) ? '' : `${label} is not valid`;
};

const ALLOWED_DOCUMENT_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.pdf', '.doc', '.docx'] as const;
const ALLOWED_DOCUMENT_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

const getFileExtension = (fileName: string) => {
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex >= 0 ? fileName.slice(lastDotIndex).toLowerCase() : '';
};

const validateDocumentFile = (file: File | null | undefined): string | null => {
  if (!file) {
    return 'Please select a file to upload';
  }

  const fileExtension = getFileExtension(file.name);
  if (!ALLOWED_DOCUMENT_EXTENSIONS.includes(fileExtension as (typeof ALLOWED_DOCUMENT_EXTENSIONS)[number])) {
    return 'Unsupported file type. Only JPG, PNG, PDF, DOC, and DOCX files are allowed';
  }

  if (file.type && !ALLOWED_DOCUMENT_MIME_TYPES.has(file.type)) {
    return 'Unsupported file format detected. Please upload a valid JPG, PNG, PDF, DOC, or DOCX file';
  }

  return null;
};

const MAX_EMPLOYEE_CODE_LENGTH = 10;
const PASSPORT_NUMBER_REGEX = /^[A-Z][0-9]{7}$/;
const PASSPORT_NUMBER_HELPER_TEXT = 'Format: 1 letter followed by 7 digits (e.g., A1234567)';

type SuperiorFieldKey = 'supervisorId' | 'hrManagerId' | 'mentorId' | 'reviewerId';
type SuperiorRoleSelectionMap = Record<string, string>;

const normalizeRoleName = (value: string) => value.trim().toLowerCase();

const getSuperiorFieldForRole = (roleName: string): SuperiorFieldKey | null => {
  const normalized = normalizeRoleName(roleName);

  if (normalized.includes('hr')) return 'hrManagerId';
  if (normalized.includes('mentor')) return 'mentorId';
  if (normalized.includes('reviewer')) return 'reviewerId';
  if (
    normalized.includes('reporting manager') ||
    normalized.includes('supervisor') ||
    (normalized.includes('manager') && !normalized.includes('hr'))
  ) {
    return 'supervisorId';
  }

  return null;
};

const validateEmployeeCode = (employeeCode: string): string => {
  const trimmedCode = employeeCode.trim().toUpperCase();

  if (!trimmedCode) {
    return 'Employee code is required.';
  }

  if (trimmedCode.length > MAX_EMPLOYEE_CODE_LENGTH) {
    return `Employee code must be at most ${MAX_EMPLOYEE_CODE_LENGTH} characters.`;
  }

  return '';
};

const getPassportNumberError = (passportNumber: string): string => {
  const normalizedPassportNumber = passportNumber.trim().toUpperCase();

  if (!normalizedPassportNumber) {
    return '';
  }

  return PASSPORT_NUMBER_REGEX.test(normalizedPassportNumber)
    ? ''
    : PASSPORT_NUMBER_HELPER_TEXT;
};

const getApiErrorMessage = (error: any, fallback: string) => {
  const data = error?.response?.data;

  if (typeof data === 'string') {
    return data;
  }

  return data?.message || error?.message || fallback;
};

const EmployeeProfile: React.FC = () => {
  // ...existing code...
  // For displaying generated password after sending login instructions
  const [showGeneratedPassword, setShowGeneratedPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [pincodeTouched, setPincodeTouched] = useState(false);
  const { id, section } = useParams<{ id: string; section?: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedEmployee: employee, loading } = useSelector((state: RootState) => state.employees);
  const [activeSection, setActiveSection] = useState(section || 'basic');
  const profileBasePath = location.pathname.startsWith('/my-profile/')
    ? '/my-profile'
    : '/admin/employee/manage';
  const [saving, setSaving] = useState(false);
  const [profileImageUploading, setProfileImageUploading] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | undefined>(undefined);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Password dialog
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Send Login Instruction
  const [sendingInstruction, setSendingInstruction] = useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [loginDialogEmail, setLoginDialogEmail] = useState('');

  // ─── Dropdown Master Data ─────────────────────────────────────────
  const [genders, setGenders] = useState<DropdownItem[]>([]);
  const [bloodGroups, setBloodGroups] = useState<DropdownItem[]>([]);
  const [maritalStatuses, setMaritalStatuses] = useState<DropdownItem[]>([]);
  const [nationalities, setNationalities] = useState<DropdownItem[]>([]);
  const [religions, setReligions] = useState<DropdownItem[]>([]);
  const [categories, setCategories] = useState<DropdownItem[]>([]);
  const [employeeStatuses, setEmployeeStatuses] = useState<DropdownItem[]>([]);
  const [employmentTypes, setEmploymentTypes] = useState<DropdownItem[]>([]);
  const [organizations, setOrganizations] = useState<DropdownItem[]>([]);
  const [departments, setDepartments] = useState<DropdownItem[]>([]);
  const [designations, setDesignations] = useState<DropdownItem[]>([]);
  const [branches, setBranches] = useState<DropdownItem[]>([]);
  const [shifts, setShifts] = useState<DropdownItem[]>([]);
  const [salaryStructures, setSalaryStructures] = useState<DropdownItem[]>([]);
  const [employeeProfileRoleMappings, setEmployeeProfileRoleMappings] = useState<EmployeeProfileRoleLookupDto[]>([]);
  const [relationships, setRelationships] = useState<DropdownItem[]>([]);
  // Use DocumentTypeItem[] for document types with config
  const [documentTypes, setDocumentTypes] = useState<DocumentTypeItem[]>([]);
  const [attendanceTypes, setAttendanceTypes] = useState<DropdownItem[]>([]);
  const [countries, setCountries] = useState<DropdownItem[]>([]);
  const [states, setStates] = useState<DropdownItem[]>([]);
  const [cities, setCities] = useState<DropdownItem[]>([]);
  const [roles, setRoles] = useState<DropdownItem[]>([]);
  const [emailGroups, setEmailGroups] = useState<DropdownItem[]>([]);

  // ─── Form State ───────────────────────────────────────────────────

  // Basic Details 1
  const [basicForm1, setBasicForm1] = useState({
    employeeCode: '', fullName: '', firstName: '', middleName: '', lastName: '',
    gender: '', bloodGroup: '', dateOfBirth: '', maritalStatus: '',
  });
  const [employeeCodeError, setEmployeeCodeError] = useState('');

  // Basic Details 2
  const [basicForm2, setBasicForm2] = useState({
    nationality: '', religion: '', category: '',
    aadhaarNumber: '', panNumber: '', passportNumber: '', drivingLicense: '', uanNumber: '',
  });

  // Basic Details 3
  const [basicForm3, setBasicForm3] = useState({
    joinedDate: '', confirmationDate: '', employmentStatus: 0, employmentType: '', organizationId: '',
  });

  // Login
  const [loginEmail, setLoginEmail] = useState('');
  const [biometricUserId, setBiometricUserId] = useState('');

  // Job Details
  const [jobForm, setJobForm] = useState({
    departmentId: '', designationId: '', branchId: '', shiftId: '',
    salaryStructureId: '', workLocation: '', probationPeriod: 0,
  });

  // Contact Details
  const [contactForm, setContactForm] = useState({
    mobileNumber: '', alternateMobile: '', personalEmail: '', officialEmail: '',
    addressLine1: '', addressLine2: '', city: '', state: '', country: '', pincode: '',
  });

  // Superior Mapping
  const [superiorForm, setSuperiorForm] = useState({
    supervisorId: '', hrManagerId: '', mentorId: '', reviewerId: '',
  });
  const [selectedSuperiorByRole, setSelectedSuperiorByRole] = useState<SuperiorRoleSelectionMap>({});

  // Settings
  const [settingsForm, setSettingsForm] = useState({
    allowMobileCheckin: false, allowMultipleCheckin: false, restrictLocationCheckin: false,
    allowFaceAttendance: false,
    allowOvertime: false, allowLateMark: false,
    attendanceTracking: '', payrollEnabled: false, isActive: true,
  });

  // Roles
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);

  // Emergency Contact (add form)
  const [emergencyForm, setEmergencyForm] = useState({
    contactName: '', relationship: '', mobileNumber: '', alternateNumber: '',
  });
  const [editingEmergencyContactId, setEditingEmergencyContactId] = useState<string | null>(null);

  // Dependent (add form)
  const [dependentForm, setDependentForm] = useState({
    dependentName: '', relationship: '', dateOfBirth: '', occupation: '',
  });
  const [editingDependentId, setEditingDependentId] = useState<string | null>(null);

  // Email Subscriptions
  const [emailSubForm, setEmailSubForm] = useState({
    subscribePayrollEmails: false, subscribeLeaveEmails: false,
    subscribeAttendanceEmails: false, subscribeAnnouncements: false,
  });

  // Subscriber Group (add form)
  const [subscriberGroupForm, setSubscriberGroupForm] = useState({
    groupName: '', subscriberEmail: '', isActive: true,
  });
  const [editingSubscriberGroupId, setEditingSubscriberGroupId] = useState<string | null>(null);

  // Document upload form
  const [docForm, setDocForm] = useState({
    documentType: '', documentName: '', documentNumber: '',
    issueDate: '', expiryDate: '', remarks: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentFileError, setDocumentFileError] = useState('');

  // Handles profile image file selection and validation
  const handleProfileImageChange = (file: File | null) => {
    if (!file) {
      setSelectedFile(null);
      setDocumentFileError('');
      return;
    }
    // Only allow image types for profile image
    const allowedImageTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedImageTypes.includes(file.type)) {
      setSelectedFile(null);
      setDocumentFileError('Only image files (JPG, PNG, GIF, WEBP) are allowed');
      return;
    }
    setSelectedFile(file);
    setDocumentFileError('');
  };

  // Document action state and handlers
  const [viewDoc, setViewDoc] = useState<any>(null);
  const [editDoc, setEditDoc] = useState<any>(null);
  const [deleteDocId, setDeleteDocId] = useState<string | null>(null);
  const [docEditForm, setDocEditForm] = useState<any>(null);
  const [docActionLoading, setDocActionLoading] = useState(false);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ uanNumber?: string } & Record<string, string>>({});
  const handleViewDocument = (doc: any) => setViewDoc(doc);
  const handleEditDocument = (doc: any) => {
    setEditDoc(doc);
    setDocEditForm({
      documentNumber: doc.documentNumber || '',
      issueDate: doc.issueDate ? doc.issueDate.split('T')[0] : '',
      expiryDate: doc.expiryDate ? doc.expiryDate.split('T')[0] : '',
      remarks: doc.remarks || '',
    });
  };
  const handleDeleteDocument = (docId: string) => setDeleteDocId(docId);
  const handleDownloadDocument = async (doc: any) => {
    if (!doc?.id) return;

    setDownloadingDocId(doc.id);
    try {
      const result = await employeeApi.getDocumentDownloadUrl(doc.id);
      const documentUrl = getDocumentUrl(result.url || doc.filePath);
      if (!documentUrl) {
        showError('Document file not found');
        return;
      }

      const link = document.createElement('a');
      link.href = documentUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.download = doc.documentName || 'employee-document';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      showError(getApiErrorMessage(err, 'Failed to download document'));
    } finally {
      setDownloadingDocId(null);
    }
  };

  // ─── Load Dropdowns ───────────────────────────────────────────────
  const user = useSelector((state: RootState) => state.auth.user);
  const tenantId = user?.tenantId;

  // ── Role-based visibility ───────────────────────────────────────────────────
  // Reads LoginResponse.Roles (full list) and LoginResponse.RoleName (primary)
  // exactly as your backend sends them via JWT → Redux.
  const ADMIN_ROLES = ['Admin', 'HR', 'Manager', 'SuperAdmin'];

  const userRoles: string[] = Array.isArray(user?.roles)
    ? user?.roles ?? []
    : user?.roleName
      ? [user.roleName]
      : [];
  const isAdmin = userRoles.some((r) =>
    ADMIN_ROLES.some((a) => a.toLowerCase() === r.toLowerCase())
  );

  const isEmployeeUser = useMemo(() => {
  const roles = user?.roles ?? [];

  return (
    roles.some(role => normalizeRoleName(role) === 'employee') ||
    normalizeRoleName(user?.roleName ?? '') === 'employee'
  );
  }, [user?.roleName, user?.roles]);

  // Sidebar shows only what the current user is allowed to see
  const profileSections = ALL_PROFILE_SECTIONS.filter(
    (sec) => isAdmin || !ADMIN_ONLY_SECTION_KEYS.has(sec.key)
  );

  const genderOptions = useMemo(
    () => mergeSelectedOption(genders, getSelectedOption(employee?.genderId || employee?.gender, employee?.genderName)),
    [genders, employee?.gender, employee?.genderId, employee?.genderName]
  );
  const bloodGroupOptions = useMemo(
    () => mergeSelectedOption(bloodGroups, getSelectedOption(employee?.bloodGroupId || employee?.bloodGroup, employee?.bloodGroupName)),
    [bloodGroups, employee?.bloodGroup, employee?.bloodGroupId, employee?.bloodGroupName]
  );
  const maritalStatusOptions = useMemo(
    () => mergeSelectedOption(maritalStatuses, getSelectedOption(employee?.maritalStatusId || employee?.maritalStatus, employee?.maritalStatusName)),
    [maritalStatuses, employee?.maritalStatus, employee?.maritalStatusId, employee?.maritalStatusName]
  );
  const nationalityOptions = useMemo(
    () => mergeSelectedOption(nationalities, getSelectedOption(employee?.nationalityId || employee?.nationality, employee?.nationalityName)),
    [nationalities, employee?.nationality, employee?.nationalityId, employee?.nationalityName]
  );
  const religionOptions = useMemo(
    () => mergeSelectedOption(religions, getSelectedOption(employee?.religionId || employee?.religion, employee?.religionName)),
    [religions, employee?.religion, employee?.religionId, employee?.religionName]
  );
  const categoryOptions = useMemo(
    () => mergeSelectedOption(categories, getSelectedOption(employee?.categoryId || employee?.category, employee?.categoryName)),
    [categories, employee?.category, employee?.categoryId, employee?.categoryName]
  );
  const organizationOptions = useMemo(
    () => mergeSelectedOption(organizations, getSelectedOption(employee?.organizationId, employee?.organizationName)),
    [organizations, employee?.organizationId, employee?.organizationName]
  );
  const branchOptions = useMemo(
    () => mergeSelectedOption(branches, getSelectedOption(employee?.branchId, employee?.branchName)),
    [branches, employee?.branchId, employee?.branchName]
  );
  const departmentOptions = useMemo(
    () => mergeSelectedOption(departments, getSelectedOption(employee?.departmentId, employee?.departmentName)),
    [departments, employee?.departmentId, employee?.departmentName]
  );
  const designationOptions = useMemo(
    () => mergeSelectedOption(designations, getSelectedOption(employee?.designationId, employee?.designationName)),
    [designations, employee?.designationId, employee?.designationName]
  );
  const shiftOptions = useMemo(
    () => mergeSelectedOption(shifts, getSelectedOption(employee?.shiftId, employee?.shiftName)),
    [shifts, employee?.shiftId, employee?.shiftName]
  );
  const cityOptions = useMemo(
    () => mergeSelectedOption(cities, getSelectedOption(employee?.cityId || employee?.city, employee?.cityName)),
    [cities, employee?.city, employee?.cityId, employee?.cityName]
  );
  const stateOptions = useMemo(
    () => mergeSelectedOption(states, getSelectedOption(employee?.stateId || employee?.state, employee?.stateName)),
    [states, employee?.state, employee?.stateId, employee?.stateName]
  );
  const countryOptions = useMemo(
    () => mergeSelectedOption(countries, getSelectedOption(employee?.countryId || employee?.country, employee?.countryName)),
    [countries, employee?.country, employee?.countryId, employee?.countryName]
  );
  // ────────────────────────────────────────────────────────────────────────────
  const loadDropdowns = useCallback(async () => {
    try {
      const [
        g, bg, ms, nat, rel, cat, es, et,
        dept, desg, br, sal,
        rels, docT, attT, ctry, st, ct, allRoles, roleMappings, eg
      ] = await Promise.all([
        masterApi.getGenders(), masterApi.getBloodGroups(), masterApi.getMaritalStatus(),
        masterApi.getNationality(), masterApi.getReligion(), masterApi.getCategory(),
        masterApi.getEmployeeStatus(), masterApi.getEmploymentType(),
        masterApi.getDepartments(), masterApi.getDesignations(), masterApi.getBranches(),
        masterApi.getSalaryStructures(),
        masterApi.getRelationships(), documentManagementApi.getDocumentTypes(), masterApi.getAttendanceTypes(),
        masterApi.getCountries(), masterApi.getStates(), masterApi.getCities(),
        masterApi.getRoles(), rolesApi.getEmployeeProfileRoles(), masterApi.getEmailGroups()
      ]);
      setGenders(g); setBloodGroups(bg); setMaritalStatuses(ms);
      setNationalities(nat); setReligions(rel); setCategories(cat);
      setEmployeeStatuses(es); setEmploymentTypes(et);
      setDepartments(dept); setDesignations(desg); setBranches(br);
      setSalaryStructures(sal);
      setRelationships(rels); setDocumentTypes(docT); setAttendanceTypes(attT);
      setCountries(ctry); setStates(st); setCities(ct); setRoles(allRoles); setEmployeeProfileRoleMappings(roleMappings); setEmailGroups(eg);

      if (tenantId) {
        try {
          const organizationItems = await administrationApi.getOrganizations(tenantId);
          setOrganizations(
            (organizationItems || []).map((item) => ({
              id: item.id,
              name: item.organizationFullName || item.organizationShortName || '',
            }))
          );
        } catch {
          setOrganizations([]);
        }
      } else {
        setOrganizations([]);
      }

      if (tenantId) {
        try {
          const items = await shiftApi.getShifts(tenantId);
          setShifts(
            (items || []).map((s: any) => ({
              id: s.id,
              name: s.shiftName || s.name || '',
            }))
          );
        } catch {
          setShifts([]);
        }
      } else {
        setShifts([]);
      }
    } catch {
      // silently continue with empty dropdowns
    }
  }, [tenantId]);

  useEffect(() => {
    if (!basicForm3.organizationId) {
      masterApi.getBranches().then(setBranches).catch(() => setBranches([]));
      return;
    }

    masterApi.getBranches(basicForm3.organizationId)
      .then((items) => {
        const branchItems = mergeSelectedOption(
          items,
          basicForm3.organizationId === employee?.organizationId
            ? getSelectedOption(employee?.branchId, employee?.branchName)
            : null
        );

        setBranches(branchItems);
        setJobForm((current) => {
          if (!current.branchId || branchItems.some((item) => String(item.id) === current.branchId)) {
            return current;
          }

          return { ...current, branchId: '', departmentId: '', designationId: '' };
        });
      })
      .catch(() => setBranches([]));
  }, [basicForm3.organizationId, employee?.organizationId, employee?.branchId, employee?.branchName]);

  useEffect(() => {
    if (!jobForm.branchId) {
      setDepartments([]);
      setJobForm((current) => current.departmentId || current.designationId ? { ...current, departmentId: '', designationId: '' } : current);
      return;
    }

    masterApi.getDepartments(jobForm.branchId)
      .then((items) => {
        const departmentItems = mergeSelectedOption(
          items,
          jobForm.branchId === employee?.branchId
            ? getSelectedOption(employee?.departmentId, employee?.departmentName)
            : null
        );

        setDepartments(departmentItems);
        setJobForm((current) => {
          if (!current.departmentId || departmentItems.some((item) => String(item.id) === current.departmentId)) {
            return current;
          }

          return { ...current, departmentId: '', designationId: '' };
        });
      })
      .catch(() => setDepartments([]));
  }, [jobForm.branchId, employee?.branchId, employee?.departmentId, employee?.departmentName]);

  useEffect(() => {
    if (!jobForm.departmentId) {
      setDesignations([]);
      setJobForm((current) => current.designationId ? { ...current, designationId: '' } : current);
      return;
    }

    masterApi.getDesignations(jobForm.departmentId)
      .then((items) => {
        const designationItems = mergeSelectedOption(
          items,
          jobForm.departmentId === employee?.departmentId
            ? getSelectedOption(employee?.designationId, employee?.designationName)
            : null
        );

        setDesignations(designationItems);
        setJobForm((current) => {
          if (!current.designationId || designationItems.some((item) => String(item.id) === current.designationId)) {
            return current;
          }

          return { ...current, designationId: '' };
        });
      })
      .catch(() => setDesignations([]));
  }, [jobForm.departmentId, employee?.departmentId, employee?.designationId, employee?.designationName]);

  useEffect(() => {
    loadDropdowns();
  }, [loadDropdowns]);

  useEffect(() => {
    if (section && section !== activeSection) setActiveSection(section);
  }, [section]);

  useEffect(() => {
    if (id) dispatch(fetchEmployeeProfile(id));
    return () => { dispatch(clearSelectedEmployee()); };
  }, [dispatch, id]);

  useEffect(() => {
    let cancelled = false;

    if (!employee?.id || !employee.profileImagePath) {
      setProfilePhotoUrl(undefined);
      return;
    }

    employeeApi.getProfileImageUrl(employee.id)
      .then((result) => {
        if (!cancelled) {
          setProfilePhotoUrl(result.url);
        }
      })
      .catch((err) => {
        console.error('Failed to load profile image URL:', err);
        if (!cancelled) {
          setProfilePhotoUrl(undefined);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [employee?.id, employee?.profileImagePath]);

  const handleUANChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Allow only digits
    if (!/^\d*$/.test(value)) return;

    // Limit to 12 digits
    if (value.length > 12) return;

    setBasicForm2({ ...basicForm2, uanNumber: value });

    // Validate
    if (value.length !== 12) {
      setErrors((prev) => ({ ...prev, uanNumber: "" }));
    } else {
      setErrors((prev) => ({ ...prev, uanNumber: "" }));
    }
  };

  // Populate forms when employee data loads
  useEffect(() => {
    if (!employee) return;
    setBasicForm1({
      employeeCode: employee.employeeCode || '',
      fullName: employee.fullName || '',
      firstName: employee.firstName || '',
      middleName: employee.middleName || '',
      lastName: employee.lastName || '',
      gender: employee.genderId || employee.gender || '',
      bloodGroup: employee.bloodGroupId || employee.bloodGroup || '',
      dateOfBirth: employee.dateOfBirth ? employee.dateOfBirth.split('T')[0] : '',
      maritalStatus: employee.maritalStatusId || employee.maritalStatus || '',
    });
    setEmployeeCodeError('');
    setBasicForm2({
      nationality: employee.nationalityId || employee.nationality || '',
      religion: employee.religionId || employee.religion || '',
      category: employee.categoryId || employee.category || '',
      aadhaarNumber: employee.aadhaarNumber || '',
      panNumber: employee.panNumber || '',
      passportNumber: employee.passportNumber || '',
      drivingLicense: employee.drivingLicense || '',
      uanNumber: employee.uanNumber || '',
    });
    setBasicForm3({
      joinedDate: employee.joinedDate ? employee.joinedDate.split('T')[0] : '',
      confirmationDate: employee.confirmationDate ? employee.confirmationDate.split('T')[0] : '',
      employmentStatus: employee.employmentStatus !== undefined && employee.employmentStatus !== null ? Number(employee.employmentStatus) : 0,
      employmentType: employee.employmentType || '',
      organizationId: employee.organizationId || '',
    });
    setLoginEmail(employee.officialEmail || employee.email || '');
    setBiometricUserId(employee.biometricUserId || '');
    setJobForm({
      departmentId: employee.departmentId || '',
      designationId: employee.designationId || '',
      branchId: employee.branchId || '',
      shiftId: employee.shiftId || '',
      salaryStructureId: employee.salaryStructureId || '',
      workLocation: employee.workLocation || '',
      probationPeriod: employee.probationPeriod || 0,
    });
    setContactForm({
      mobileNumber: employee.mobileNumber || '',
      alternateMobile: employee.alternateMobile || '',
      personalEmail: employee.personalEmail || '',
      officialEmail: employee.officialEmail || '',
      addressLine1: employee.addressLine1 || '',
      addressLine2: employee.addressLine2 || '',
      city: employee.cityId || employee.city || '',
      state: employee.stateId || employee.state || '',
      country: employee.countryId || employee.country || '',
      pincode: employee.pincode || '',
    });
    setSuperiorForm({
      supervisorId: employee.supervisorId || employee.reportingManagerId || '',
      hrManagerId: employee.hrManagerId || '',
      mentorId: employee.mentorId || '',
      reviewerId: employee.reviewerId || '',
    });
    setSelectedSuperiorByRole(() => {
      const selections: SuperiorRoleSelectionMap = {};

      employeeProfileRoleMappings.forEach((roleGroup) => {
        const fieldKey = getSuperiorFieldForRole(roleGroup.roleName);
        if (!fieldKey) return;

        const selectedValue =
          fieldKey === 'supervisorId'
            ? (employee.supervisorId || employee.reportingManagerId || '')
            : (employee[fieldKey] || '');

        if (selectedValue) {
          selections[roleGroup.roleId] = String(selectedValue);
        }
      });

      return selections;
    });
    setSettingsForm({
      allowMobileCheckin: employee.allowMobileCheckin,
      allowMultipleCheckin: employee.allowMultipleCheckin,
      allowFaceAttendance: employee.allowFaceAttendance,
      restrictLocationCheckin: employee.restrictLocationCheckin,
      allowOvertime: employee.allowOvertime,
      allowLateMark: employee.allowLateMark,
      attendanceTracking: employee.attendanceTracking || '',
      payrollEnabled: employee.payrollEnabled,
      isActive: employee.isActive,
    });
    setEmailSubForm({
      subscribePayrollEmails: employee.subscribePayrollEmails,
      subscribeLeaveEmails: employee.subscribeLeaveEmails,
      subscribeAttendanceEmails: employee.subscribeAttendanceEmails,
      subscribeAnnouncements: employee.subscribeAnnouncements,
    });
    if (employee.roles) {
      setSelectedRoleIds(employee.roles.map((r) => r.roleId));
    }
  }, [employee, employeeProfileRoleMappings]);

  // ─── Save Handlers ────────────────────────────────────────────────
  const showSuccess = (msg: string) => { setSuccessMsg(msg); setErrorMsg(null); };
  const showError = (msg: string) => { setErrorMsg(msg); setSuccessMsg(null); };
  const reload = () => { if (id) dispatch(fetchEmployeeProfile(id)); };

  const checkDuplicateEmployeeCode = useCallback(async (employeeCode: string, employeeId: string) => {
    const matchingEmployees = await employeeApi.getAll(1, 50, employeeCode);
    return matchingEmployees.some(
      (existingEmployee) =>
        existingEmployee.id !== employeeId &&
        existingEmployee.employeeCode?.trim().toUpperCase() === employeeCode
    );
  }, []);

  const handleSaveBasicDetails = async () => {
    if (!id) return;

    const normalizedEmployeeCode = basicForm1.employeeCode.trim().toUpperCase();
    const validationError = validateEmployeeCode(normalizedEmployeeCode);
    if (validationError) {
      setEmployeeCodeError(validationError);
      showError(validationError);
      return;
    }

    const normalizedPassportNumber = basicForm2.passportNumber.trim().toUpperCase();
    const passportNumberError = getPassportNumberError(normalizedPassportNumber);
    if (passportNumberError) {
      setErrors((prev) => ({ ...prev, passportNumber: passportNumberError }));
      showError(passportNumberError);
      return;
    }

    setSaving(true);
    try {
      const isDuplicateEmployeeCode = await checkDuplicateEmployeeCode(normalizedEmployeeCode, id);
      if (isDuplicateEmployeeCode) {
        const duplicateMessage = 'Employee code already exists. Please use a unique employee code.';
        setEmployeeCodeError(duplicateMessage);
        showError(duplicateMessage);
        return;
      }

      await employeeApi.updateBasicDetails(id, {
        id,
        employeeCode: normalizedEmployeeCode,
        fullName: basicForm1.fullName,
        firstName: basicForm1.firstName || undefined,
        middleName: basicForm1.middleName || undefined,
        lastName: basicForm1.lastName || undefined,
        gender: basicForm1.gender || undefined,
        bloodGroup: basicForm1.bloodGroup || undefined,
        dateOfBirth: basicForm1.dateOfBirth || undefined,
        maritalStatus: basicForm1.maritalStatus || undefined,
        nationality: basicForm2.nationality || undefined,
        religion: basicForm2.religion || undefined,
        category: basicForm2.category || undefined,
        aadhaarNumber: basicForm2.aadhaarNumber || undefined,
        panNumber: basicForm2.panNumber || undefined,
        passportNumber: normalizedPassportNumber || undefined,
        drivingLicense: basicForm2.drivingLicense || undefined,
        uanNumber: basicForm2.uanNumber || undefined,
        confirmationDate: basicForm3.confirmationDate || undefined,
        employmentType: basicForm3.employmentType || undefined,
        organizationId: basicForm3.organizationId || undefined,
        joinedDate: basicForm3.joinedDate || undefined,
        employmentStatus: Number(basicForm3.employmentStatus),
      });
      setBasicForm2((prev) => ({ ...prev, passportNumber: normalizedPassportNumber }));
      setErrors((prev) => ({ ...prev, passportNumber: '' }));
      setBasicForm1((prev) => ({ ...prev, employeeCode: normalizedEmployeeCode }));
      setEmployeeCodeError('');
      showSuccess('Basic details updated successfully');
      if (user?.employeeId === id) {
        dispatch(setUser({ fullName: basicForm1.fullName }));
      }
      reload();
    } catch (err: any) {
      const apiMessage = err?.response?.data?.message || 'Failed to update basic details';
      if (typeof apiMessage === 'string' && apiMessage.toLowerCase().includes('employee code')) {
        setEmployeeCodeError(apiMessage);
      }
      showError(apiMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveJobDetails = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await employeeApi.updateJobDetails(id, {
        id,
        departmentId: jobForm.departmentId || undefined,
        designationId: jobForm.designationId || undefined,
        branchId: jobForm.branchId || undefined,
        shiftId: jobForm.shiftId || undefined,
        salaryStructureId: jobForm.salaryStructureId || undefined,
        workLocation: jobForm.workLocation || undefined,
        probationPeriod: jobForm.probationPeriod || undefined,
        joinedDate: basicForm3.joinedDate || undefined,
      });
      showSuccess('Job details updated successfully');
      reload();
    } catch {
      showError('Failed to update job details');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveContactDetails = async () => {
    if (contactForm.pincode && !/^\d{6}$/.test(contactForm.pincode)) {
      toast.error('Pincode must be exactly 6 digits');
      return;
    }
    if (!id) return;
    const personalEmailError = getEmailError(contactForm.personalEmail, 'Personal email');
    const officialEmailError = getEmailError(contactForm.officialEmail, 'Official email');
    // Prevent duplicate emails within this employee
    if (
      contactForm.personalEmail &&
      (contactForm.personalEmail === contactForm.officialEmail || contactForm.personalEmail === loginEmail)
    ) {
      showError('Personal email must match Official or Login email');
      return;
    }
    if (
      contactForm.officialEmail &&
      (contactForm.officialEmail !== loginEmail)
    ) {
      showError('Official email must match Login email');
      return;
    }
    if (
      loginEmail &&
      (loginEmail !== contactForm.officialEmail)
    ) {
      showError('Login email must match Official email');
      return;
    }
    if (personalEmailError || officialEmailError) {
      showError(personalEmailError || officialEmailError);
      return;
    }
    // Check for global uniqueness in DB
    try {
      const allEmployees = await employeeApi.getAll(1, 10000); // adjust page size as needed
      const isDuplicate = allEmployees.some(emp =>
        emp.id !== id && (
          (contactForm.personalEmail && (emp.personalEmail === contactForm.personalEmail || emp.officialEmail === contactForm.personalEmail || emp.email === contactForm.personalEmail)) ||
          (contactForm.officialEmail && (emp.personalEmail === contactForm.officialEmail || emp.officialEmail === contactForm.officialEmail || emp.email === contactForm.officialEmail)) ||
          (loginEmail && (emp.personalEmail === loginEmail || emp.officialEmail === loginEmail || emp.email === loginEmail))
        )
      );
      if (isDuplicate) {
        showError('Email already exists for another employee');
        return;
      }
    } catch (err) {
      showError('Failed to validate email uniqueness');
      return;
    }
    setSaving(true);
    try {
      // await employeeApi.updateContactDetails(id, {
      //   id,
      //   mobileNumber: contactForm.mobileNumber || undefined,
      //   alternateMobile: contactForm.alternateMobile || undefined,
      //   personalEmail: contactForm.personalEmail || undefined,
      //   officialEmail: contactForm.officialEmail || undefined,
      //   email: contactForm.officialEmail || undefined,
      // });

      const payload = {
        id,
        mobileNumber: contactForm.mobileNumber,
        alternateMobile: contactForm.alternateMobile,
        personalEmail: contactForm.personalEmail,
        officialEmail: contactForm.officialEmail,
        email: contactForm.officialEmail,

        addressLine1: contactForm.addressLine1,
        addressLine2: contactForm.addressLine2,
        city: contactForm.city,
        state: contactForm.state,
        country: contactForm.country,
        pincode: contactForm.pincode,
      };
      await employeeApi.updateContactDetails(id, payload);

      // Sync loginEmail if officialEmail changed
      if (contactForm.officialEmail && contactForm.officialEmail !== loginEmail) {
        setLoginEmail(contactForm.officialEmail);
      }
      showSuccess('Contact details updated successfully');
      reload();
    } catch (err: any) {
      showError(err?.response?.data?.message || 'Failed to update contact details');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSupervisor = async () => {
    if (!id) return;

    const nextSuperiorForm = {
      supervisorId: '',
      hrManagerId: '',
      mentorId: '',
      reviewerId: '',
    };

    employeeProfileRoleMappings.forEach((roleGroup) => {
      const fieldKey = getSuperiorFieldForRole(roleGroup.roleName);
      if (!fieldKey) return;
      nextSuperiorForm[fieldKey] = selectedSuperiorByRole[roleGroup.roleId] || '';
    });

    setSuperiorForm(nextSuperiorForm);
    setSaving(true);
    try {
      await employeeApi.updateSupervisor(id, {
        id,
        supervisorId: nextSuperiorForm.supervisorId || undefined,
        hrManagerId: nextSuperiorForm.hrManagerId || undefined,
        mentorId: nextSuperiorForm.mentorId || undefined,
        reviewerId: nextSuperiorForm.reviewerId || undefined,
      });
      showSuccess('Superior mapping updated successfully');
      reload();
    } catch (err: any) {
      showError(getApiErrorMessage(err, 'Failed to update superior mapping'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await employeeApi.updateSettings(id, { id, ...settingsForm });
      showSuccess('Employee settings updated successfully');
      reload();
    } catch {
      showError('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRoles = async () => {
    if (!id) return;
    const uniqueRoleIds = Array.from(new Set(selectedRoleIds.filter(Boolean)));

    setSaving(true);
    try {
      await employeeApi.updateRoles(id, { id, roleIds: uniqueRoleIds });
      showSuccess('Roles updated successfully');
      reload();
    } catch {
      showError('Failed to update roles');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEmailSubscriptions = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await employeeApi.updateEmailSubscriptions(id, { id, ...emailSubForm });
      showSuccess('Email subscriptions updated successfully');
      reload();
    } catch {
      showError('Failed to update email subscriptions');
    } finally {
      setSaving(false);
    }
  };

  const handleAddEmergencyContact = async () => {
    if (!id || !emergencyForm.contactName || !emergencyForm.relationship) {
      showError('Contact name and relationship are required');
      return;
    }
    setSaving(true);
    try {
      if (editingEmergencyContactId) {
        await employeeApi.updateEmergencyContact(editingEmergencyContactId, { id: editingEmergencyContactId, ...emergencyForm });
        showSuccess('Emergency contact updated');
      } else {
        await employeeApi.addEmergencyContact(id, { employeeId: id, ...emergencyForm });
        showSuccess('Emergency contact added');
      }
      setEmergencyForm({ contactName: '', relationship: '', mobileNumber: '', alternateNumber: '' });
      setEditingEmergencyContactId(null);
      reload();
    } catch {
      showError(`Failed to ${editingEmergencyContactId ? 'update' : 'add'} emergency contact`);
    } finally {
      setSaving(false);
    }
  };

  const handleEditEmergencyContact = (contact: any) => {
    setEditingEmergencyContactId(contact.id);
    setEmergencyForm({
      contactName: contact.contactName || '',
      relationship: contact.relationship || '',
      mobileNumber: contact.mobileNumber || '',
      alternateNumber: contact.alternateNumber || '',
    });
  };

  const handleCancelEmergencyEdit = () => {
    setEditingEmergencyContactId(null);
    setEmergencyForm({ contactName: '', relationship: '', mobileNumber: '', alternateNumber: '' });
  };

  const handleDeleteEmergencyContact = async (contactId: string) => {
    setSaving(true);
    try {
      await employeeApi.deleteEmergencyContact(contactId);
      showSuccess('Emergency contact deleted');
      reload();
    } catch {
      showError('Failed to delete emergency contact');
    } finally {
      setSaving(false);
    }
  };

  const handleAddDependent = async () => {
    if (!id || !dependentForm.dependentName || !dependentForm.relationship) {
      showError('Dependent name and relationship are required');
      return;
    }
    setSaving(true);
    try {
      if (editingDependentId) {
        await employeeApi.updateDependent(editingDependentId, { id: editingDependentId, ...dependentForm });
        showSuccess('Dependent updated');
      } else {
        await employeeApi.addDependent(id, { employeeId: id, ...dependentForm });
        showSuccess('Dependent added');
      }
      setDependentForm({ dependentName: '', relationship: '', dateOfBirth: '', occupation: '' });
      setEditingDependentId(null);
      reload();
    } catch {
      showError(`Failed to ${editingDependentId ? 'update' : 'add'} dependent`);
    } finally {
      setSaving(false);
    }
  };

  const handleEditDependent = (dependent: any) => {
    setEditingDependentId(dependent.id);
    setDependentForm({
      dependentName: dependent.dependentName || '',
      relationship: dependent.relationship || '',
      dateOfBirth: dependent.dateOfBirth ? dependent.dateOfBirth.split('T')[0] : '',
      occupation: dependent.occupation || '',
    });
  };

  const handleCancelDependentEdit = () => {
    setEditingDependentId(null);
    setDependentForm({ dependentName: '', relationship: '', dateOfBirth: '', occupation: '' });
  };

  const handleDeleteDependent = async (depId: string) => {
    setSaving(true);
    try {
      await employeeApi.deleteDependent(depId);
      showSuccess('Dependent deleted');
      reload();
    } catch {
      showError('Failed to delete dependent');
    } finally {
      setSaving(false);
    }
  };

  const handleAddSubscriberGroup = async () => {
    if (!id || !subscriberGroupForm.groupName) return;
    const subscriberEmailError = getEmailError(subscriberGroupForm.subscriberEmail, 'Subscriber email');
    if (subscriberEmailError) {
      showError(subscriberEmailError);
      return;
    }
    setSaving(true);
    try {
      if (editingSubscriberGroupId) {
        await employeeApi.updateSubscriberGroup(editingSubscriberGroupId, { id: editingSubscriberGroupId, ...subscriberGroupForm });
        showSuccess('Subscriber group updated');
      } else {
        await employeeApi.addSubscriberGroup(id, { employeeId: id, ...subscriberGroupForm });
        showSuccess('Subscriber group added');
      }
      setSubscriberGroupForm({ groupName: '', subscriberEmail: '', isActive: true });
      setEditingSubscriberGroupId(null);
      reload();
    } catch {
      showError(`Failed to ${editingSubscriberGroupId ? 'update' : 'add'} subscriber group`);
    } finally {
      setSaving(false);
    }
  };

  const handleEditSubscriberGroup = (group: any) => {
    setEditingSubscriberGroupId(group.id);
    setSubscriberGroupForm({
      groupName: group.groupName || '',
      subscriberEmail: group.subscriberEmail || '',
      isActive: group.isActive ?? true,
    });
  };

  const handleCancelSubscriberGroupEdit = () => {
    setEditingSubscriberGroupId(null);
    setSubscriberGroupForm({ groupName: '', subscriberEmail: '', isActive: true });
  };

  const handleDeleteSubscriberGroup = async (groupId: string) => {
    setSaving(true);
    try {
      await employeeApi.deleteSubscriberGroup(groupId);
      showSuccess('Subscriber group deleted');
      reload();
    } catch {
      showError('Failed to delete subscriber group');
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentFileChange = (file: File | null) => {
    const validationError = validateDocumentFile(file);

    if (validationError) {
      setSelectedFile(null);
      setDocumentFileError(validationError);
      return;
    }

    setSelectedFile(file);
    setDocumentFileError('');
  };

  const handleUploadDocument = async () => {
    if (!id || !docForm.documentType || !selectedFile) return;
    const fileValidationError = validateDocumentFile(selectedFile);
    if (fileValidationError) {
      setDocumentFileError(fileValidationError);
      showError(fileValidationError);
      return;
    }

    const isInvalidDate =
      docForm.issueDate &&
      docForm.expiryDate &&
      docForm.issueDate > docForm.expiryDate;

    if (isInvalidDate) {
      Swal.fire("Expiry date must be after issue date");
      return; // ❌ STOP submission
    }

    setSaving(true);
    try {
      await employeeApi.uploadDocument(id, selectedFile, docForm);
      showSuccess('Document uploaded');
      setDocForm({ documentType: '', documentName: '', documentNumber: '', issueDate: '', expiryDate: '', remarks: '' });
      setSelectedFile(null);
      setDocumentFileError('');
      reload();
    } catch {
      showError('Failed to upload document');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLoginDetails = async () => {
    if (!id) return;
    const loginEmailError = getEmailError(loginEmail);
    if (loginEmailError) {
      showError(loginEmailError);
      return;
    }
    // Check for global uniqueness in DB
    try {
      const allEmployees = await employeeApi.getAll(1, 10000); // adjust page size as needed
      const isDuplicate = allEmployees.some(emp =>
        emp.id !== id && (
          (loginEmail && (emp.personalEmail === loginEmail || emp.officialEmail === loginEmail || emp.email === loginEmail))
        )
      );
      if (isDuplicate) {
        showError('Email already exists for another employee');
        return;
      }
    } catch (err) {
      showError('Failed to validate email uniqueness');
      return;
    }
    setSaving(true);
    try {
      await employeeApi.updateLoginDetails(id, loginEmail);
      // Sync officialEmail if loginEmail changed
      if (loginEmail && loginEmail !== contactForm.officialEmail) {
        setContactForm((prev) => ({ ...prev, officialEmail: loginEmail }));
      }
      showSuccess('Login details updated');
      reload();
    } catch {
      showError('Failed to update login details');
    } finally {
      setSaving(false);
    }

    // // Profile image upload handler (use selectedFile)
    // const handleUploadProfileImage = async () => {
    //   if (!id || !selectedFile) return;
    //   setProfileImageUploading(true);
    //   try {
    //     await employeeApi.uploadProfileImage(id, selectedFile);
    //     showSuccess('Profile image updated successfully');
    //     reload();
    //   } catch {
    //     showError('Failed to upload profile image');
    //   } finally {
    //     setProfileImageUploading(false);
    //   }
    // };
  }
  const validatePassword = (password: string): string | null => {
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) return 'Password must contain at least one special character';
    return null;
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    if (!id) {
      setPasswordError('Employee ID is missing');
      return;
    }
    setSaving(true);
    try {
      await employeeApi.changePassword(id, newPassword);
      showSuccess('Password changed successfully');
      setPasswordDialogOpen(false);
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError(null);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to change password';
      setPasswordError(msg);
    } finally {
      setSaving(false);
    }
  };

  const isInvalidDate = Boolean(
    docForm.issueDate &&
    docForm.expiryDate &&
    docForm.issueDate > docForm.expiryDate
  );

  const handleSaveBiometric = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await employeeApi.updateBiometricMapping(id, biometricUserId);
      showSuccess('Biometric mapping updated');
      reload();
    } catch {
      showError('Failed to update biometric mapping');
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name: string) =>
    name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

  // ─── Loading ──────────────────────────────────────────────────────
  if (loading || !employee) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  // ─── Section Renderers ────────────────────────────────────────────

  // 1. Roles
  const renderRoles = () => {
    const employeeProfileRoleOptions = employeeProfileRoleMappings.map((role) => ({
      id: role.roleId,
      name: role.roleName,
    }));

    return (
      <Box>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Roles</Typography>
        <FormControl fullWidth size="small" sx={{ maxWidth: 520 }}>
          <InputLabel id="employee-role-select-label">Roles</InputLabel>
          <Select
            labelId="employee-role-select-label"
            multiple
            value={selectedRoleIds}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedRoleIds(typeof value === 'string' ? value.split(',') : (value as string[]));
            }}
            input={<OutlinedInput label="Roles" />}
            renderValue={(selected) =>
              employeeProfileRoleOptions
                .filter((role) => (selected as string[]).includes(String(role.id)))
                .map((role) => role.name)
                .join(', ')
            }
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 320,
                },
              },
            }}
          >
            {employeeProfileRoleOptions.map((role) => (
              <MenuItem key={String(role.id)} value={String(role.id)}>
                <Checkbox checked={selectedRoleIds.includes(String(role.id))} />
                <ListItemText primary={role.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Only roles with Employee Profile enabled in Role Master are shown here.
        </Typography>
        {employee.roles && employee.roles.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Current Roles:</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {employee.roles.map((r) => (
                <Chip key={r.id} label={r.roleName} color="primary" size="small" />
              ))}
            </Box>
          </Box>
        )}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveRoles} disabled={saving} sx={{ textTransform: 'none' }}>
            Save Roles
          </Button>
        </Box>
      </Box>
    );
  };

  const renderDocumentActionDialogs = () => (
    <>
      {/* View Document Dialog */}
      <Dialog open={!!viewDoc} onClose={() => setViewDoc(null)} maxWidth="sm" fullWidth>
        <DialogTitle>View Document</DialogTitle>
        <DialogContent dividers>
          {viewDoc && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography><b>Type:</b> {(() => {
                const docType = documentTypes.find(dt => String(dt.id) === String(viewDoc.documentType));
                return docType ? docType.documentTypeName : viewDoc.documentType;
              })()}</Typography>
              <Typography><b>Number:</b> {viewDoc.documentNumber || '-'}</Typography>
              <Typography><b>Issue Date:</b> {viewDoc.issueDate ? new Date(viewDoc.issueDate).toLocaleDateString() : '-'}</Typography>
              <Typography><b>Expiry Date:</b> {viewDoc.expiryDate ? new Date(viewDoc.expiryDate).toLocaleDateString() : '-'}</Typography>
              <Typography><b>Remarks:</b> {viewDoc.remarks || '-'}</Typography>
              <Typography component="div">
                <b>File:</b>{' '}
                {viewDoc.filePath ? (
                  <Button
                    size="small"
                    onClick={() => handleDownloadDocument(viewDoc)}
                    disabled={downloadingDocId === viewDoc.id}
                  >
                    Download/View
                  </Button>
                ) : '-'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDoc(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Document Dialog */}
      <Dialog open={!!editDoc} onClose={() => { setEditDoc(null); setDocEditForm(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Document</DialogTitle>
        <DialogContent dividers>
          {editDoc && docEditForm && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Document Number" fullWidth size="small" value={docEditForm.documentNumber}
                onChange={e => setDocEditForm({ ...docEditForm, documentNumber: e.target.value })} />
              <TextField label="Issue Date" type="date" fullWidth size="small" value={docEditForm.issueDate}
                onChange={e => setDocEditForm({ ...docEditForm, issueDate: e.target.value })} InputLabelProps={{ shrink: true }} />
              <TextField label="Expiry Date" type="date" fullWidth size="small" value={docEditForm.expiryDate}
                onChange={e => setDocEditForm({ ...docEditForm, expiryDate: e.target.value })} InputLabelProps={{ shrink: true }} />
              <TextField label="Remarks" fullWidth size="small" value={docEditForm.remarks}
                onChange={e => setDocEditForm({ ...docEditForm, remarks: e.target.value })} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDoc(null); setDocEditForm(null); }}>Cancel</Button>
          <Button variant="contained" disabled={docActionLoading} onClick={async () => {
            if (!editDoc || !docEditForm) return;
            setDocActionLoading(true);
            try {
              await employeeApi.updateDocument(editDoc.id, {
                documentName: editDoc.documentName,
                documentType: editDoc.documentType,
                documentNumber: docEditForm.documentNumber,
                issueDate: docEditForm.issueDate,
                expiryDate: docEditForm.expiryDate,
                remarks: docEditForm.remarks,
                filePath: editDoc.filePath,
                fileSize: editDoc.fileSize,
              });
              setSuccessMsg('Document updated successfully');
              setEditDoc(null); setDocEditForm(null);
              reload();
            } catch {
              setErrorMsg('Failed to update document');
            } finally {
              setDocActionLoading(false);
            }
          }}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Document Dialog */}
      <Dialog open={!!deleteDocId} onClose={() => setDeleteDocId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to delete this document?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDocId(null)}>Cancel</Button>
          <Button variant="contained" color="error" disabled={docActionLoading} onClick={async () => {
            if (!deleteDocId) return;
            setDocActionLoading(true);
            try {
              await employeeApi.deleteDocument(deleteDocId);
              setSuccessMsg('Document deleted successfully');
              setDeleteDocId(null);
              reload();
            } catch {
              setErrorMsg('Failed to delete document');
            } finally {
              setDocActionLoading(false);
            }
          }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );

  // 2. Basic Details (merged)
  const renderBasicDetails = () => (
    <Box>
      {renderDocumentActionDialogs()}

      {/* Basic Details 1 */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Basic Details</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            label="Employee Code"
            fullWidth
            size="small"
            value={basicForm1.employeeCode}
            onChange={(e) => {
              const normalizedValue = e.target.value.toUpperCase().slice(0, MAX_EMPLOYEE_CODE_LENGTH);
              setBasicForm1({ ...basicForm1, employeeCode: normalizedValue });
              setEmployeeCodeError(validateEmployeeCode(normalizedValue));
            }}
            error={!!employeeCodeError}
            helperText={employeeCodeError}
            inputProps={{ maxLength: MAX_EMPLOYEE_CODE_LENGTH }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="Full Name" fullWidth size="small" value={basicForm1.fullName}
            onChange={(e) => setBasicForm1({ ...basicForm1, fullName: e.target.value })} />
        </Grid>
        {/* <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="Middle Name" fullWidth size="small" value={basicForm1.middleName}
            onChange={(e) => setBasicForm1({ ...basicForm1, middleName: e.target.value })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="Last Name" fullWidth size="small" value={basicForm1.lastName}
            onChange={(e) => setBasicForm1({ ...basicForm1, lastName: e.target.value })} />
        </Grid> */}
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField label="Gender" value={basicForm1.gender} options={genderOptions}
            onChange={(v) => setBasicForm1({ ...basicForm1, gender: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField label="Blood Group" value={basicForm1.bloodGroup} options={bloodGroupOptions}
            onChange={(v) => setBasicForm1({ ...basicForm1, bloodGroup: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="Date of Birth" type="date" fullWidth size="small"
            value={basicForm1.dateOfBirth}
            onChange={(e) => setBasicForm1({ ...basicForm1, dateOfBirth: e.target.value })}
            inputProps={{ max: new Date().toISOString().split('T')[0] }}
            InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField label="Marital Status" value={basicForm1.maritalStatus} options={maritalStatusOptions}
            onChange={(v) => setBasicForm1({ ...basicForm1, maritalStatus: v })} />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Nationality, Religion, IDs */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>Identity & Background</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField label="Nationality" value={basicForm2.nationality} options={nationalityOptions}
            onChange={(v) => setBasicForm2({ ...basicForm2, nationality: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField label="Religion" value={basicForm2.religion} options={religionOptions}
            onChange={(v) => setBasicForm2({ ...basicForm2, religion: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField label="Category" value={basicForm2.category} options={categoryOptions}
            onChange={(v) => setBasicForm2({ ...basicForm2, category: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            label="Aadhaar Number"
            fullWidth
            size="small"
            value={basicForm2.aadhaarNumber}
            onChange={(e) => {
              const value = e.target.value;

              // Allow only digits and max length 12
              if (/^\d{0,12}$/.test(value)) {
                setBasicForm2({
                  ...basicForm2,
                  aadhaarNumber: value,
                });
              }
            }}
            inputProps={{
              maxLength: 12,
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
            error={
              basicForm2.aadhaarNumber.length > 0 &&
              basicForm2.aadhaarNumber.length !== 12
            }
            helperText={
              basicForm2.aadhaarNumber.length > 0 &&
                basicForm2.aadhaarNumber.length !== 12
                ? "Aadhaar number must be exactly 12 digits"
                : ""
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            label="PAN Number"
            fullWidth
            size="small"
            value={basicForm2.panNumber}
            onChange={(e) => {
              let value = e.target.value.toUpperCase();

              // Allow only alphanumeric and max 10 chars
              if (/^[A-Z0-9]{0,10}$/.test(value)) {
                setBasicForm2({ ...basicForm2, panNumber: value });
              }
            }}
            inputProps={{ maxLength: 10 }}
            error={
              basicForm2.panNumber.length > 0 &&
              !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(basicForm2.panNumber)
            }
            helperText={
              basicForm2.panNumber.length > 0 &&
                !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(basicForm2.panNumber)
                ? "Example:ABCDE1234F"
                : ""
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            label="Passport Number"
            fullWidth
            size="small"
            value={basicForm2.passportNumber}
            onChange={(e) => {
              const value = e.target.value.trim().toUpperCase();
              if (!/^[A-Z0-9]{0,8}$/.test(value)) return;

              setBasicForm2({ ...basicForm2, passportNumber: value });
              setErrors((prev) => ({
                ...prev,
                passportNumber: getPassportNumberError(value),
              }));
            }}
            inputProps={{ maxLength: 8 }}
            error={!!errors.passportNumber}
            helperText={errors.passportNumber || PASSPORT_NUMBER_HELPER_TEXT}
            placeholder="A1234567"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            label="Driving License"
            fullWidth
            size="small"
            value={basicForm2.drivingLicense}
            error={!!errors.drivingLicense}
            helperText={
              errors.drivingLicense ||
              "Format: SSRRYYYYNNNNNNN (e.g., MH1420110012345)"
            }
            onChange={(e) => {
              let value = e.target.value.toUpperCase();

              // allow only alphanumeric
              if (!/^[A-Z0-9]*$/.test(value)) return;

              setBasicForm2((prev) => ({
                ...prev,
                drivingLicense: value,
              }));

              let error = "";

              // strict format check
              const dlRegex = /^[A-Z]{2}[0-9]{2}[0-9]{4}[0-9]{7}$/;

              if (value.length > 0 && !dlRegex.test(value)) {
                error =
                  "Must follow valid format (e.g., SSRRYYYYNNNNNNN, like MH1420110012345)";
              }

              setErrors((prev) => ({
                ...prev,
                drivingLicense: error,
              }));
            }}
            inputProps={{ maxLength: 15 }}
            placeholder="MH1420110012345"
          />
        </Grid>
        {/* <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="UAN Number" fullWidth size="small" value={basicForm2.uanNumber}
            onChange={(e) => setBasicForm2({ ...basicForm2, uanNumber: e.target.value })} />
        </Grid> */}
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            label="UAN Number"
            fullWidth
            size="small"
            value={basicForm2.uanNumber}
            onChange={handleUANChange}
            error={!!errors.uanNumber}
            helperText={errors.uanNumber || ''}
          />
        </Grid>

      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Employment Dates & Status */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>Employment Info</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField label="Organization" value={basicForm3.organizationId} options={organizationOptions}
            onChange={(v) => {
              setBasicForm3({ ...basicForm3, organizationId: v });
              setJobForm({ ...jobForm, branchId: '', departmentId: '', designationId: '' });
            }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="Joining Date" type="date" fullWidth size="small"
            value={basicForm3.joinedDate}
            onChange={(e) => setBasicForm3({ ...basicForm3, joinedDate: e.target.value })}
            InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="Confirmation Date" type="date" fullWidth size="small"
            value={basicForm3.confirmationDate}
            onChange={(e) => setBasicForm3({ ...basicForm3, confirmationDate: e.target.value })}
            InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField
            label="Employee Status"
            value={String(basicForm3.employmentStatus)}
            options={[
              { id: 0, name: 'Working' },
              { id: 1, name: 'Resigned' },
              { id: 2, name: 'Terminated' },
              { id: 3, name: 'Absconding' },
              { id: 4, name: 'Retired' },
              { id: 5, name: 'On Probation' },
              { id: 6, name: 'Internship' },
              { id: 7, name: 'Contract' },
              { id: 8, name: 'Freelance' },
              { id: 9, name: 'Passed Away' }
            ]}
            onChange={(v) =>
              setBasicForm3({
                ...basicForm3,
                employmentStatus: Number(v)
              })
            }
            disabled={isEmployeeUser}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField label="Employment Type" value={basicForm3.employmentType} options={employmentTypes}
            onChange={(v) => setBasicForm3({ ...basicForm3, employmentType: v })} />
        </Grid>
      </Grid>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveBasicDetails} disabled={saving} sx={{textTransform: 'none'}}>
          Save Basic Details
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* HRMS Login Details */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
        <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        HRMS Login Details
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="Username (Employee Code)" fullWidth size="small" value={employee.employeeCode} disabled
            helperText="Username is the Employee Code" />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="Email" fullWidth size="small" value={loginEmail}
            onChange={(e) => {
              setLoginEmail(e.target.value);
              // Sync to officialEmail if changed from login section
              setContactForm((prev) => ({ ...prev, officialEmail: e.target.value }));
            }}
            error={!!getEmailError(loginEmail)}
            helperText={getEmailError(loginEmail)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            label="Password"
            type={showLoginPassword ? 'text' : 'password'}
            fullWidth
            size="small"
            value={employee.hasLoginCredentials ? '********' : 'Not set'}
            disabled
            autoComplete="new-password"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowLoginPassword(!showLoginPassword)} size="small" edge="end">
                      {showLoginPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                    <Button size="small" startIcon={<LockIcon />} onClick={() => setPasswordDialogOpen(true)}>
                      Change
                    </Button>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
      </Grid>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveLoginDetails} disabled={saving} sx={{textTransform: 'none'}}>
          Save Login Details
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Biometric */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
        <BiometricIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Biometric Device Mapping
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="Biometric User ID" fullWidth size="small" value={biometricUserId}
            onChange={(e) => setBiometricUserId(e.target.value)} />
        </Grid>
      </Grid>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveBiometric} disabled={saving} sx={{textTransform: 'none'}}>
          Save Biometric Mapping
        </Button>
      </Box>
    </Box>
  );

  // 5. Job Details
  const renderJobDetails = () => (
    <Box>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Job Details</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField label="Branch" value={jobForm.branchId} options={branchOptions}
            onChange={(v) => setJobForm({ ...jobForm, branchId: v, departmentId: '', designationId: '' })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField label="Department" value={jobForm.departmentId} options={departmentOptions}
            onChange={(v) => setJobForm({ ...jobForm, departmentId: v, designationId: '' })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField label="Designation" value={jobForm.designationId} options={designationOptions}
            onChange={(v) => setJobForm({ ...jobForm, designationId: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField label="Shift" value={jobForm.shiftId} options={shiftOptions}
            onChange={(v) => setJobForm({ ...jobForm, shiftId: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField label="Salary Structure" value={jobForm.salaryStructureId} options={salaryStructures}
            onChange={(v) => setJobForm({ ...jobForm, salaryStructureId: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            label="Reporting Manager"
            fullWidth
            size="small"
            value={employee?.reportingManagerName || employee?.supervisorName || ''}
            InputProps={{ readOnly: true }}
            helperText="Update this in Superior Mapping"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="Work Location" fullWidth size="small" value={jobForm.workLocation}
            onChange={(e) => setJobForm({ ...jobForm, workLocation: e.target.value })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            label="Probation Period (months)"
            type="number"
            fullWidth
            size="small"
            value={jobForm.probationPeriod}
            onChange={(e) => {
              const value = e.target.value;

              // allow only numbers and max 2 digits
              if (/^\d{0,2}$/.test(value)) {
                setJobForm({
                  ...jobForm,
                  probationPeriod: value === '' ? 0 : Number(value),
                });
              }
            }}
            inputProps={{ min: 0, max: 99 }}
            error={jobForm.probationPeriod > 99}
            helperText={
              jobForm.probationPeriod > 99
                ? ""
                : ""
            }
          />
        </Grid>
      </Grid>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveJobDetails} disabled={saving} sx={{textTransform: 'none'}}>
          Save Job Details
        </Button>
      </Box>
    </Box>
  );

  // 6. Contact Details
  const renderContactDetails = () => (
    <Box>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Contact Details</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            label="Mobile Number"
            fullWidth
            size="small"
            value={contactForm.mobileNumber}
            inputProps={{ maxLength: 10 }}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, ""); // remove non-numeric
              setContactForm({ ...contactForm, mobileNumber: value });
            }}
            error={
              contactForm.mobileNumber.length > 0 &&
              contactForm.mobileNumber.length !== 10
            }
            helperText={
              contactForm.mobileNumber.length > 0 &&
                contactForm.mobileNumber.length !== 10
                ? "Mobile number must be 10 digits"
                : ""
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            label="Alternate Mobile"
            fullWidth
            size="small"
            value={contactForm.alternateMobile}
            inputProps={{
              maxLength: 10,
              inputMode: "numeric",
              pattern: "[0-9]*"
            }}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              setContactForm({ ...contactForm, alternateMobile: value });
            }}
            error={
              !!contactForm.alternateMobile &&
              contactForm.alternateMobile.length !== 10
            }
            helperText={
              contactForm.alternateMobile &&
                contactForm.alternateMobile.length !== 10
                ? "Alternate mobile must be 10 digits"
                : ""
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="Personal Email" fullWidth size="small" value={contactForm.personalEmail}
            onChange={(e) => setContactForm({ ...contactForm, personalEmail: e.target.value })}
            error={!!getEmailError(contactForm.personalEmail, 'Personal email')}
            helperText={getEmailError(contactForm.personalEmail, 'Personal email')} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="Official Email" fullWidth size="small" value={contactForm.officialEmail}
            onChange={(e) => {
              setContactForm({ ...contactForm, officialEmail: e.target.value });
              // Sync to loginEmail if changed from contact section
              setLoginEmail(e.target.value);
            }}
            error={!!getEmailError(contactForm.officialEmail, 'Official email')}
            helperText={getEmailError(contactForm.officialEmail, 'Official email')} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="Address Line 1" fullWidth size="small" value={contactForm.addressLine1}
            onChange={(e) => setContactForm({ ...contactForm, addressLine1: e.target.value })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="Address Line 2" fullWidth size="small" value={contactForm.addressLine2}
            onChange={(e) => setContactForm({ ...contactForm, addressLine2: e.target.value })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField label="City" value={contactForm.city} options={cityOptions}
            onChange={(v) => setContactForm({ ...contactForm, city: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField label="State" value={contactForm.state} options={stateOptions}
            onChange={(v) => setContactForm({ ...contactForm, state: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField label="Country" value={contactForm.country} options={countryOptions}
            onChange={(v) => setContactForm({ ...contactForm, country: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            label="Pincode"
            fullWidth
            size="small"
            value={contactForm.pincode}
            onChange={(e) => {
              const value = e.target.value;

              // mark as touched when user types
              if (!pincodeTouched) setPincodeTouched(true);

              // Allow only numbers and max 6 digits
              if (/^\d{0,6}$/.test(value)) {
                setContactForm({ ...contactForm, pincode: value });
              }
            }}
            error={pincodeTouched && contactForm.pincode.length > 0 && contactForm.pincode.length !== 6}
            helperText={
              pincodeTouched && contactForm.pincode.length > 0 && contactForm.pincode.length !== 6
                ? 'Pincode must be 6 digits'
                : ''
            }
            inputProps={{ maxLength: 6 }}
          />
        </Grid>
      </Grid>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveContactDetails} disabled={saving} sx={{textTransform: 'none'}}>
          Save Contact Details
        </Button>
      </Box>
    </Box>
  );

  // 7. Superior Mapping
  const renderSuperiorMapping = () => {
    const superiorFields = employeeProfileRoleMappings.map((roleGroup) => ({
      roleId: roleGroup.roleId,
      label: roleGroup.roleName,
      fieldKey: getSuperiorFieldForRole(roleGroup.roleName),
      options: roleGroup.employees.map((employeeOption) => ({
        id: employeeOption.id,
        name: employeeOption.name,
      })),
    }));

    return (
      <Box>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Superior Mapping</Typography>
        {superiorFields.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No role is enabled in Role Master for Employee Profile.
          </Alert>
        ) : (
          <>
            <Grid container spacing={2}>
              {superiorFields.map((field) => (
                <Grid key={field.roleId} size={{ xs: 12, sm: 3 }}>
                  <DropdownField
                    label={field.label}
                    value={selectedSuperiorByRole[field.roleId] || ''}
                    options={field.options.filter((option) => String(option.id) !== String(employee?.id))}
                    onChange={(value) =>
                      setSelectedSuperiorByRole((prev) => ({
                        ...prev,
                        [field.roleId]: value,
                      }))
                    }
                  />
                </Grid>
              ))}
            </Grid>
            {superiorFields.some((field) => !field.fieldKey) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Some enabled roles do not map to the current superior fields. Use role names like Reporting Manager, HR Manager, Mentor, or Reviewer for persistence.
              </Alert>
            )}
          </>
        )}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveSupervisor} disabled={saving} sx={{textTransform: 'none'}}>
            Save Superior Mapping
          </Button>
        </Box>
      </Box>
    );
  };

  // 8. Employee Settings
  const renderSettings = () => (
    <Box>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Employee Settings</Typography>
      <Box sx={{ maxWidth: 360, mb: 2 }}>
        <LanguagePreferenceSelect />
      </Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <FormControlLabel
            control={<Switch checked={settingsForm.allowOvertime}
              onChange={(e) => setSettingsForm({ ...settingsForm, allowOvertime: e.target.checked })} />}
            label="Allow Overtime"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <FormControlLabel
            control={<Switch checked={settingsForm.allowLateMark}
              onChange={(e) => setSettingsForm({ ...settingsForm, allowLateMark: e.target.checked })} />}
            label="Allow Late Mark"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField label="Attendance Tracking" value={settingsForm.attendanceTracking} options={attendanceTypes}
            onChange={(v) => setSettingsForm({ ...settingsForm, attendanceTracking: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <FormControlLabel
            control={<Switch checked={settingsForm.payrollEnabled}
              onChange={(e) => setSettingsForm({ ...settingsForm, payrollEnabled: e.target.checked })} />}
            label="Payroll Enabled"
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <FormControlLabel
            control={<Switch checked={settingsForm.allowMobileCheckin}
              onChange={(e) => setSettingsForm({ ...settingsForm, allowMobileCheckin: e.target.checked })} />}
            label="Allow Mobile Checkin"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <FormControlLabel
            control={<Switch checked={settingsForm.restrictLocationCheckin}
              onChange={(e) => setSettingsForm({ ...settingsForm, restrictLocationCheckin: e.target.checked })} />}
            label="Restrict Location Checkin"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <FormControlLabel
            control={<Switch checked={settingsForm.allowMultipleCheckin}
              onChange={(e) => setSettingsForm({ ...settingsForm, allowMultipleCheckin: e.target.checked })} />}
            label="Allow Multiple Check-in/Check-out"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <FormControlLabel
            control={<Switch checked={settingsForm.allowFaceAttendance}
              onChange={(e) => setSettingsForm({ ...settingsForm, allowFaceAttendance: e.target.checked })} />}
            label="Allow Face Attendance"
          />
        </Grid>
        {/* <Grid size={{ xs: 12, sm: 3 }}>
          <FormControlLabel
            control={<Switch checked={settingsForm.isActive}
              onChange={(e) => setSettingsForm({ ...settingsForm, isActive: e.target.checked })} />}
            label="Is Active"
          />
        </Grid> */}
      </Grid>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveSettings} disabled={saving} sx={{textTransform: 'none'}}>
          Save Settings
        </Button>
      </Box>
    </Box>
  );

  // 9. Emergency Contact
  const renderEmergencyContacts = () => (
    <Box>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Emergency Contacts</Typography>
      {employee.emergencyContacts && employee.emergencyContacts.length > 0 && (
        <Table size="small" sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>Contact Name</TableCell>
              <TableCell>Relationship</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Alternate</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employee.emergencyContacts.map((c) => (
              <TableRow key={c.id}>
                <TableCell>{c.contactName}</TableCell>
                <TableCell>{getRelationshipLabel(c.relationship, relationships)}</TableCell>
                <TableCell>{c.mobileNumber || '-'}</TableCell>
                <TableCell>{c.alternateNumber || '-'}</TableCell>
                <TableCell>
                  <IconButton color="primary" size="small" onClick={() => handleEditEmergencyContact(c)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton color="error" size="small" onClick={() => handleDeleteEmergencyContact(c.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{editingEmergencyContactId ? 'Edit Emergency Contact' : 'Add New Emergency Contact'}</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="Contact Name" fullWidth size="small" value={emergencyForm.contactName}
            onChange={(e) => setEmergencyForm({ ...emergencyForm, contactName: e.target.value })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField label="Relationship" value={emergencyForm.relationship} options={relationships}
            onChange={(v) => setEmergencyForm({ ...emergencyForm, relationship: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="Mobile Number" fullWidth size="small" value={emergencyForm.mobileNumber}
            onChange={(e) => setEmergencyForm({ ...emergencyForm, mobileNumber: normalizeNumericInput(e.target.value, 10) })}
            inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }}
            error={!!emergencyForm.mobileNumber && emergencyForm.mobileNumber.length !== 10}
            helperText={emergencyForm.mobileNumber && emergencyForm.mobileNumber.length !== 10 ? 'Mobile number must be 10 digits' : ' '} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="Alternate Number" fullWidth size="small" value={emergencyForm.alternateNumber}
            onChange={(e) => setEmergencyForm({ ...emergencyForm, alternateNumber: normalizeNumericInput(e.target.value, 10) })}
            inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }}
            error={!!emergencyForm.alternateNumber && emergencyForm.alternateNumber.length !== 10}
            helperText={emergencyForm.alternateNumber && emergencyForm.alternateNumber.length !== 10 ? 'Alternate number must be 10 digits' : ' '} />
        </Grid>
      </Grid>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        {editingEmergencyContactId && (
          <Button sx={{ mr: 1 }} onClick={handleCancelEmergencyEdit} disabled={saving}>
            Cancel
          </Button>
        )}
        <Button variant="contained" startIcon={editingEmergencyContactId ? <SaveIcon /> : <AddIcon />} onClick={handleAddEmergencyContact} 
          disabled={saving || !emergencyForm.contactName || !emergencyForm.relationship} sx={{textTransform: 'none'}}>
          {editingEmergencyContactId ? 'Update Emergency Contact' : 'Add Emergency Contact'}
        </Button>
      </Box>
    </Box>
  );

  // 10. Dependents
  const renderDependents = () => (
    <Box>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Dependants Details</Typography>
      {employee.dependents && employee.dependents.length > 0 && (
        <Table size="small" sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Relationship</TableCell>
              <TableCell>Date of Birth</TableCell>
              <TableCell>Occupation</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employee.dependents.map((d) => (
              <TableRow key={d.id}>
                <TableCell>{d.dependentName}</TableCell>
                <TableCell>{getRelationshipLabel(d.relationship, relationships)}</TableCell>
                <TableCell>{d.dateOfBirth ? new Date(d.dateOfBirth).toLocaleDateString() : '-'}</TableCell>
                <TableCell>{d.occupation || '-'}</TableCell>
                <TableCell>
                  <IconButton color="primary" size="small" onClick={() => handleEditDependent(d)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton color="error" size="small" onClick={() => handleDeleteDependent(d.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{editingDependentId ? 'Edit Dependent' : 'Add New Dependent'}</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="Dependent Name" fullWidth size="small" value={dependentForm.dependentName}
            onChange={(e) => setDependentForm({ ...dependentForm, dependentName: e.target.value })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField label="Relationship" value={dependentForm.relationship} options={relationships}
            onChange={(v) => setDependentForm({ ...dependentForm, relationship: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            label="Date of Birth"
            type="date"
            fullWidth
            size="small"
            value={dependentForm.dateOfBirth}
            onChange={(e) =>
              setDependentForm({
                ...dependentForm,
                dateOfBirth: e.target.value,
              })
            }
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: new Date().toISOString().split("T")[0],
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="Occupation" fullWidth size="small" value={dependentForm.occupation}
            onChange={(e) => setDependentForm({ ...dependentForm, occupation: e.target.value })} />
        </Grid>
      </Grid>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        {editingDependentId && (
          <Button sx={{ mr: 1 }} onClick={handleCancelDependentEdit} disabled={saving}>
            Cancel
          </Button>
        )}
        <Button variant="contained" startIcon={editingDependentId ? <SaveIcon /> : <AddIcon />} onClick={handleAddDependent}
          disabled={saving || !dependentForm.dependentName || !dependentForm.relationship} sx={{textTransform: 'none'}}>
          {editingDependentId ? 'Update Dependent' : 'Add Dependent'}
        </Button>
      </Box>
    </Box>
  );

  // 11. Email Subscription
  const renderEmailSubscription = () => (
    <Box>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Email Subscription</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <FormControlLabel
            control={<Checkbox checked={emailSubForm.subscribePayrollEmails}
              onChange={(e) => setEmailSubForm({ ...emailSubForm, subscribePayrollEmails: e.target.checked })} />}
            label="Payroll Emails"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <FormControlLabel
            control={<Checkbox checked={emailSubForm.subscribeLeaveEmails}
              onChange={(e) => setEmailSubForm({ ...emailSubForm, subscribeLeaveEmails: e.target.checked })} />}
            label="Leave Emails"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <FormControlLabel
            control={<Checkbox checked={emailSubForm.subscribeAttendanceEmails}
              onChange={(e) => setEmailSubForm({ ...emailSubForm, subscribeAttendanceEmails: e.target.checked })} />}
            label="Attendance Emails"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <FormControlLabel
            control={<Checkbox checked={emailSubForm.subscribeAnnouncements}
              onChange={(e) => setEmailSubForm({ ...emailSubForm, subscribeAnnouncements: e.target.checked })} />}
            label="Announcements"
          />
        </Grid>
      </Grid>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveEmailSubscriptions} disabled={saving} sx={{textTransform: 'none'}}>
          Save Email Subscriptions
        </Button>
      </Box>
    </Box>
  );

  // 12. Subscriber Groups
  const renderSubscriberGroups = () => (
    <Box>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Subscriber Groups</Typography>
      {employee.subscriberGroups && employee.subscriberGroups.length > 0 && (
        <Table size="small" sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>Group Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employee.subscriberGroups.map((g) => (
              <TableRow key={g.id}>
                <TableCell>{g.groupName}</TableCell>
                <TableCell>{g.subscriberEmail || '-'}</TableCell>
                <TableCell>{g.isActive ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  <IconButton color="primary" size="small" onClick={() => handleEditSubscriberGroup(g)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton color="error" size="small" onClick={() => handleDeleteSubscriberGroup(g.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{editingSubscriberGroupId ? 'Edit Subscriber Group' : 'Add New Subscriber Group'}</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DropdownField label="Group Name" value={subscriberGroupForm.groupName} options={emailGroups}
            onChange={(v) => setSubscriberGroupForm({ ...subscriberGroupForm, groupName: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField label="Subscriber Email" fullWidth size="small" value={subscriberGroupForm.subscriberEmail}
            onChange={(e) => setSubscriberGroupForm({ ...subscriberGroupForm, subscriberEmail: e.target.value })}
            error={!!getEmailError(subscriberGroupForm.subscriberEmail, 'Subscriber email')}
            helperText={getEmailError(subscriberGroupForm.subscriberEmail, 'Subscriber email')} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <FormControlLabel
            control={<Checkbox checked={subscriberGroupForm.isActive}
              onChange={(e) => setSubscriberGroupForm({ ...subscriberGroupForm, isActive: e.target.checked })} />}
            label="Is Active"
          />
        </Grid>
      </Grid>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        {editingSubscriberGroupId && (
          <Button sx={{ mr: 1 }} onClick={handleCancelSubscriberGroupEdit} disabled={saving}>
            Cancel
          </Button>
        )}
        <Button variant="contained" startIcon={editingSubscriberGroupId ? <SaveIcon /> : <AddIcon />} onClick={handleAddSubscriberGroup} disabled={saving || !subscriberGroupForm.groupName}>
          {editingSubscriberGroupId ? 'Update Subscriber Group' : 'Add Subscriber Group'}
        </Button>
      </Box>
    </Box>
  );

  // 13. Documents
  const renderDocuments = () => (
    <Box>
      {renderDocumentActionDialogs()}

      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Documents</Typography>
      {employee.employeeDocuments && employee.employeeDocuments.length > 0 && (
        <Table size="small" sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Doc Number</TableCell>
              <TableCell>Issue Date</TableCell>
              <TableCell>Expiry Date</TableCell>
              <TableCell>Remarks</TableCell>
              <TableCell>File</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employee.employeeDocuments.map((d) => {
              const docType = documentTypes.find(dt => String(dt.id) === String(d.documentType));
              return (
                <TableRow key={d.id}>
                  <TableCell>{docType ? docType.documentTypeName : d.documentType}</TableCell>
                  <TableCell>{d.documentNumber || '-'}</TableCell>
                  <TableCell>{d.issueDate ? new Date(d.issueDate).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>{d.expiryDate ? new Date(d.expiryDate).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>{d.remarks || '-'}</TableCell>
                  <TableCell>
                    {d.filePath ? (
                      <Button
                        size="small"
                        variant="text"
                        disabled={downloadingDocId === d.id}
                        onClick={async (e) => {
                          e.stopPropagation();
                          await handleDownloadDocument(d);
                        }}
                      >
                        {downloadingDocId === d.id ? 'Opening...' : 'Download'}
                      </Button>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="primary"
                      title="Edit"
                      onClick={e => { e.stopPropagation(); handleEditDocument(d); }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        title="Delete"
                        onClick={e => { e.stopPropagation(); handleDeleteDocument(d.id); }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Upload New Document</Typography>

      {/* Dynamic Document Type Field Rendering */}
      {(() => {
        // Find selected document type config
        // Typecast to DocumentTypeItem[] for config lookup
        const selectedDocType = documentTypes.find(dt => String(dt.id) === String(docForm.documentType));
        // If you add more config flags (e.g., isDateOfIssueApplicable), extend this destructure
        const expiryRequired = selectedDocType?.expiryRequired;
        // const issueRequired = selectedDocType?.issueRequired; // Example for future

        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <DropdownField
                label="Document Type"
                value={docForm.documentType}
                options={documentTypes.map(dt => ({
                  id: dt.id,
                  name: dt.documentTypeName, // map to 'name'
                }))}
                onChange={(v) => setDocForm({ ...docForm, documentType: v })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Document Number" fullWidth size="small" value={docForm.documentNumber}
                onChange={(e) => setDocForm({ ...docForm, documentNumber: e.target.value })} />
            </Grid>
            {/* Conditionally render Issue Date if required (extend with more flags as needed) */}
            {/* If you add isDateOfIssueApplicable, use that here instead of always showing Issue Date */}
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="Issue Date"
                type="date"
                fullWidth
                size="small"
                value={docForm.issueDate}
                onChange={(e) =>
                  setDocForm({ ...docForm, issueDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                error={isInvalidDate}
              />
            </Grid>
            {/* Conditionally render Expiry Date if expiryRequired */}
            {expiryRequired && (
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  label="Expiry Date"
                  type="date"
                  fullWidth
                  size="small"
                  value={docForm.expiryDate}
                  onChange={(e) =>
                    setDocForm({ ...docForm, expiryDate: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  error={isInvalidDate}
                  helperText={isInvalidDate ? "Expiry must be after issue date" : ""}
                />
              </Grid>
            )}
            {/* Add more fields here as you extend Document Type config */}
            <Grid size={{ xs: 12, sm: 3 }}>
              <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} fullWidth
                sx={{ height: 40 }}
                color={documentFileError ? 'error' : 'primary'}>
                {selectedFile ? selectedFile.name : 'Select File'}
                <input
                  type="file"
                  hidden
                  accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                  onChange={(e) => {
                    handleDocumentFileChange(e.target.files?.[0] || null);
                    e.target.value = '';
                  }}
                />
              </Button>
              {documentFileError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {documentFileError}
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Remarks" fullWidth size="small" value={docForm.remarks}
                onChange={(e) => setDocForm({ ...docForm, remarks: e.target.value })} />
            </Grid>
          </Grid>
        );
      })()}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={handleUploadDocument} disabled={saving || !docForm.documentType || !selectedFile} sx={{textTransform: 'none'}}>
          Upload Document
        </Button>
      </Box>

      {/* Azure Blob Storage - Document Upload */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Azure Blob Storage Documents</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        Upload and manage documents securely using Azure Blob Storage with automatic tenant isolation
      </Typography>

      {employee.id && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {/* BGV Documents */}
          <DocumentUpload
            category={BlobCategory.BGV_DOCUMENTS}
            containerName="bgv-documents"
            entityType="bgv"
            entityId={employee.id.toString()}
            title="BGV Documents"
            maxFiles={10}
            onDocumentsChange={(docs) => {
              console.log('BGV Documents updated:', docs);
              // Optional: Save metadata to your database
            }}
          />

          {/* Leave Documents */}
          <DocumentUpload
            category={BlobCategory.LEAVE_DOCUMENTS}
            containerName="leave-documents"
            entityType="leave"
            entityId={employee.id.toString()}
            title="Leave Documents"
            maxFiles={5}
            onDocumentsChange={(docs) => {
              console.log('Leave Documents updated:', docs);
            }}
          />

          {/* General Attachments */}
          <DocumentUpload
            category={BlobCategory.GENERAL_ATTACHMENTS}
            containerName="attachments"
            entityType="employee"
            entityId={employee.id.toString()}
            title="General Attachments"
            maxFiles={10}
            onDocumentsChange={(docs) => {
              console.log('Attachments updated:', docs);
            }}
          />
        </Box>
      )}
    </Box>
  );

  // Section router
  const renderActiveSection = () => {
    // Blocks direct URL navigation to admin-only sections (e.g. typing /roles in address bar)
    if (ADMIN_ONLY_SECTION_KEYS.has(activeSection) && !isAdmin) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            You do not have permission to view this section.
          </Typography>
        </Box>
      );
    }
    switch (activeSection) {
      case 'roles': return renderRoles();
      case 'basic': return renderBasicDetails();
      case 'job': return renderJobDetails();
      case 'contact': return renderContactDetails();
      case 'supervisor': return renderSuperiorMapping();
      case 'settings': return renderSettings();
      case 'emergency': return renderEmergencyContacts();
      case 'dependants': return renderDependents();
      case 'email': return renderEmailSubscription();
      case 'groups': return renderSubscriberGroups();
      case 'documents': return renderDocuments();
      default: return null;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/employees')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight="bold">Employee Profile</Typography>
      </Box>

      {successMsg && <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 2 }}>{successMsg}</Alert>}
      {errorMsg && <Alert severity="error" onClose={() => setErrorMsg(null)} sx={{ mb: 2 }}>{errorMsg}</Alert>}

      {/* Header Summary Card */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
          {/* Profile Photo - Use Blob Storage Component */}
          {employee.id && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <EmployeeProfilePhotoUpload
                employeeId={employee.id.toString()}
                currentPhotoUrl={profilePhotoUrl}
                onPhotoUpload={async (fileInfo) => {
                  console.log('Photo uploaded:', fileInfo);
                  if (employee.id && fileInfo.url && fileInfo.file) {
                    try {
                      // Save the profile image path to the employee record
                      const result = await employeeApi.uploadProfileImage(employee.id, fileInfo.file);
                      const imageUrl = await employeeApi.getProfileImageUrl(employee.id);
                      setProfilePhotoUrl(imageUrl.url);
                      // Refresh employee data to show new photo
                      dispatch(fetchEmployeeProfile(employee.id)).catch(err => {
                        console.error('Failed to refresh profile:', err);
                      });
                      return imageUrl.url || result.profileImagePath;
                    } catch (err) {
                      console.error('Failed to save profile image:', err);
                    }
                  }
                }}
              />
            </Box>
          )}

          {/* Fallback: Simple avatar + upload if no employee ID */}
          {!employee.id && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Avatar
                src={employee.profileImagePath ? getDocumentUrl(employee.profileImagePath) : undefined}
                sx={{ width: 80, height: 80, fontSize: 28, bgcolor: '#667eea' }}
              >
                {getInitials(employee.fullName)}
              </Avatar>
              <Button
                variant="outlined"
                component="label"
                size="small"
                disabled={profileImageUploading}
                startIcon={profileImageUploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
              >
                {employee.profileImagePath ? 'Change Photo' : 'Upload Photo'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => {
                    handleProfileImageChange(e.target.files?.[0] || null);
                    e.target.value = '';
                  }}
                />
              </Button>
            </Box>
          )}

          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h5" fontWeight="bold">{employee.fullName}</Typography>
              <Chip label={employee.employeeCode} size="small" variant="outlined" color="primary" />
              <Chip
                label={employee.employmentStatusName || 'Unknown'}
                color={(() => {
                  switch ((employee.employmentStatusName || '').toLowerCase()) {
                    case 'working': return 'success';
                    case 'inactive': return 'default';
                    case 'resigned': return 'warning';
                    default: return 'default';
                  }
                })()}
                size="small"
              />
            </Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Supervisor</Typography>
                <Typography variant="body2">{employee.supervisorName || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Mobile</Typography>
                <Typography variant="body2">{employee.mobileNumber || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Joined Date</Typography>
                <Typography variant="body2">
                  {employee.joinedDate ? new Date(employee.joinedDate).toLocaleDateString() : '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Designation</Typography>
                <Typography variant="body2">{employee.designationName || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Branch</Typography>
                <Typography variant="body2">{employee.branchName || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Department</Typography>
                <Typography variant="body2">{employee.departmentName || '-'}</Typography>
              </Grid>
            </Grid>
          </Box>
          <Button
            variant="outlined"
            startIcon={sendingInstruction ? <CircularProgress size={18} /> : <SendIcon />}
            sx={{ whiteSpace: 'nowrap' }}
            disabled={sendingInstruction}
            onClick={() => {
              setLoginDialogEmail(employee?.officialEmail || employee?.personalEmail || employee?.email || '');
              setLoginDialogOpen(true);
            }}
          >
            {sendingInstruction ? 'Sending...' : 'Send Login Instruction'}
          </Button>

          {/* Send Login Instruction Dialog */}
          <Dialog open={loginDialogOpen} onClose={() => setLoginDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Send Login Instruction</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField
                  label="Enter Employee Email (Optional)"
                  fullWidth
                  size="small"
                  value={loginDialogEmail}
                  onChange={(e) => setLoginDialogEmail(e.target.value)}
                  placeholder="employee@example.com"
                  error={!!getEmailError(loginDialogEmail)}
                  helperText={getEmailError(loginDialogEmail)}
                />
                <TextField
                  label="Contact Number"
                  fullWidth
                  size="small"
                  value={employee?.mobileNumber || ''}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">+91</InputAdornment>
                    ),
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setLoginDialogOpen(false)}>Close</Button>
              <Button
                variant="contained"
                disabled={sendingInstruction}
                startIcon={sendingInstruction ? <CircularProgress size={18} /> : <SendIcon />}
                onClick={async () => {
                  if (!id) return;
                  const dialogEmailError = getEmailError(loginDialogEmail);
                  if (dialogEmailError) {
                    setErrorMsg(dialogEmailError);
                    return;
                  }
                  try {
                    setSendingInstruction(true);
                    setErrorMsg(null);
                    const result = await employeeApi.sendLoginInstruction(id, loginDialogEmail || undefined);
                    const msgs: string[] = [];
                    if (result.emailSent) msgs.push('Email sent');
                    if (result.whatsAppSent) msgs.push('WhatsApp sent');
                    if (msgs.length > 0) {
                      setSuccessMsg(`Login instruction: ${msgs.join(', ')}`);
                    } else {
                      setErrorMsg(result.errorMessage || 'No notification sent');
                    }
                    // If backend ever returns a generated password, handle here. Currently not used.
                    setGeneratedPassword(null);
                    setShowGeneratedPassword(false);
                    setLoginDialogOpen(false);
                  } catch (err: any) {
                    setErrorMsg(err?.response?.data?.message || 'Failed to send login instruction');
                  } finally {
                    setSendingInstruction(false);
                  }
                }}
              >
                {sendingInstruction ? 'Sending...' : 'Submit'}
              </Button>
              {/* Generated Password Dialog (future use, currently unreachable) */}
              {showGeneratedPassword && generatedPassword && (
                <Dialog open={showGeneratedPassword} onClose={() => setShowGeneratedPassword(false)} maxWidth="xs" fullWidth>
                  <DialogTitle>Generated Password</DialogTitle>
                  <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      This password was generated for the employee. Please copy and share securely.
                    </Alert>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <TextField
                        label="Temporary Password"
                        value={generatedPassword || ''}
                        fullWidth
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button
                                size="small"
                                onClick={() => {
                                  navigator.clipboard.writeText(generatedPassword || '');
                                }}
                              >
                                Copy
                              </Button>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setShowGeneratedPassword(false)}>Close</Button>
                  </DialogActions>
                </Dialog>
              )}
            </DialogActions>
          </Dialog>
        </Box>
      </Paper>

      {/* Main Content: Sidebar + Detail */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {/* Left Sidebar Navigation */}
        <Paper sx={{ width: 260, flexShrink: 0, borderRadius: 2 }}>
          <List dense>
            {profileSections.map((sec) => (
              <ListItemButton
                key={sec.key}
                selected={activeSection === sec.key}
                onClick={() => {
                  setActiveSection(sec.key);
                  navigate(`${profileBasePath}/${id}/${sec.key}`, { replace: true });
                }}
              >
                <ListItemIcon>{sec.icon}</ListItemIcon>
                <ListItemText primary={sec.label} />
              </ListItemButton>
            ))}
          </List>
        </Paper>
        {/* Right Content Area */}
        <Paper sx={{ flex: 1, p: 3, borderRadius: 2 }}>
          {renderActiveSection()}
        </Paper>
      </Box>

      {/* Generated Password Dialog (future use, currently unreachable) */}
      {showGeneratedPassword && generatedPassword && (
        <Dialog open={showGeneratedPassword} onClose={() => setShowGeneratedPassword(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Generated Password</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              This password was generated for the employee. Please copy and share securely.
            </Alert>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                label="Temporary Password"
                value={generatedPassword || ''}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        onClick={() => {
                          navigator.clipboard.writeText(generatedPassword || '');
                        }}
                      >
                        Copy
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowGeneratedPassword(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => { setPasswordDialogOpen(false); setPasswordError(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          {passwordError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{passwordError}</Alert>}
          <TextField
            label="Current Password"
            type={showCurrentPassword ? 'text' : 'password'}
            fullWidth
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            sx={{ mt: 1 }}
            autoComplete="new-password"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} size="small">
                      {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          <TextField
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mt: 2 }}
            helperText="Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special character"
            autoComplete="new-password"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          <TextField
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            fullWidth
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            sx={{ mt: 2 }}
            error={!!confirmPassword && confirmPassword !== newPassword}
            helperText={confirmPassword && confirmPassword !== newPassword ? 'Passwords do not match' : ''}
            autoComplete="new-password"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} size="small">
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPasswordDialogOpen(false); setPasswordError(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleChangePassword} disabled={!newPassword || !confirmPassword || saving}>
            Change Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EmployeeProfile;
